import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Download, Edit, FileText, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import SyncHeader from './SyncHeader';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ICS309Form = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    exercise_name: currentExercise?.exercise_name || '',
    operational_period_from: '',
    operational_period_to: '',
    radio_network_name: '',
    radio_operator: '',
    radio_operators: [],
    message_log: [],
    prepared_by: '',
    signature: '',
    date_time_prepared: ''
  });
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch all ICS 309 entries for current exercise
  const fetchEntries = async (silent = false) => {
    if (!currentExercise?.id) return;
    
    if (!silent) setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/ics-309?exercise_id=${currentExercise.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
        setLastSaved(new Date().toISOString());
      }
    } catch (error) {
      if (!silent) console.error('Error fetching ICS 309 entries:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentExercise?.id]);

  // Setup polling for real-time sync (every 15 seconds)
  useEffect(() => {
    if (!currentExercise?.id) return;
    
    const pollInterval = setInterval(() => {
      fetchEntries(true);
    }, 15000);
    
    return () => clearInterval(pollInterval);
  }, [currentExercise?.id]);

  // Manual refresh
  const handleRefresh = async () => {
    setHasUnsavedChanges(false);
    await fetchEntries(false);
  };

  // === Comms Log CSV Import / Export (list-level, all entries for exercise) ===
  const logFileInputRef = useRef(null);

  const handleLogExportCSV = async () => {
    if (!currentExercise?.id) return;
    try {
      const token = secureStorage.getToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API}/ics-309/${currentExercise.id}/export-csv`, { headers });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (currentExercise.exercise_name || currentExercise.name || 'exercise').replace(/[^a-z0-9]+/gi, '_');
      link.setAttribute('download', `ics-309-${safeName}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting ICS 309 CSV:', error);
      window.alert('Failed to export CSV. Please try again.');
    }
  };

  const handleLogImportClick = () => {
    if (logFileInputRef.current) logFileInputRef.current.click();
  };

  const handleLogImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentExercise?.id) return;
    try {
      const token = secureStorage.getToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const payload = new FormData();
      payload.append('file', file);
      const response = await fetch(`${API}/ics-309/${currentExercise.id}/import-csv`, {
        method: 'POST',
        headers,
        body: payload,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const result = await response.json();
      window.alert(`Import complete. ${result.created || 0} log(s) created, ${result.updated || 0} updated.`);
      await fetchEntries(false);
    } catch (error) {
      console.error('Error importing ICS 309 CSV:', error);
      window.alert(typeof error.message === 'string' ? error.message : 'Failed to import CSV. Please check the file format.');
    }
  };

  // Initialize form with editing data
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        exercise_name: editingEntry.exercise_name || currentExercise?.exercise_name || '',
        operational_period_from: editingEntry.operational_period_from || '',
        operational_period_to: editingEntry.operational_period_to || '',
        radio_network_name: editingEntry.radio_network_name || '',
        radio_operator: editingEntry.radio_operator || '',
        radio_operators: editingEntry.radio_operators || [],
        message_log: editingEntry.message_log || [],
        prepared_by: editingEntry.prepared_by || '',
        signature: editingEntry.signature || '',
        date_time_prepared: editingEntry.date_time_prepared || ''
      });
    } else {
      setFormData({
        exercise_name: currentExercise?.exercise_name || '',
        operational_period_from: '',
        operational_period_to: '',
        radio_network_name: '',
        radio_operator: '',
        radio_operators: [],
        message_log: [],
        prepared_by: '',
        signature: '',
        date_time_prepared: ''
      });
    }
  }, [editingEntry, currentExercise]);

  // Add new message log item with auto-incremented Msg # (0001 to 9999)
  const addMessageLogItem = () => {
    setFormData(prev => {
      // Find the highest existing numeric msg # and increment by 1, capped at 9999
      const existingNums = (prev.message_log || [])
        .map(it => parseInt(it.from_msg_number, 10))
        .filter(n => !isNaN(n) && n > 0);
      const nextNum = existingNums.length > 0 ? Math.max(...existingNums) + 1 : 1;
      const capped = Math.min(nextNum, 9999);
      const newItem = {
        id: Date.now().toString(),
        time: '',
        from_callsign: '',
        from_msg_number: String(capped).padStart(4, '0'),
        to_callsign: '',
        to_msg_number: '',
        message: '',
        action_type: ''
      };
      return {
        ...prev,
        message_log: [...prev.message_log, newItem]
      };
    });
  };

  // Update message log item
  const updateMessageLogItem = (index, field, value) => {
    const updatedLog = [...formData.message_log];
    updatedLog[index] = { ...updatedLog[index], [field]: value };
    setFormData(prev => ({ ...prev, message_log: updatedLog }));
  };

  // Delete message log item
  const deleteMessageLogItem = (index) => {
    setFormData(prev => ({
      ...prev,
      message_log: prev.message_log.filter((_, i) => i !== index)
    }));
  };

  // CSV/Excel Import State
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importPreview, setImportPreview] = useState({ headers: [], rows: [], mapping: {} });
  const fileInputRef = useRef(null);

  // Convert Excel serial date number to YYYY-MM-DD string
  const excelSerialToDate = (serial) => {
    if (typeof serial !== 'number') return null;
    const epoch = new Date(1899, 11, 30);
    const days = Math.floor(serial);
    const d = new Date(epoch.getTime() + days * 86400000);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  };

  // Convert Excel serial time fraction to HH:MM string
  const excelSerialToTime = (serial) => {
    if (typeof serial !== 'number') return null;
    const frac = serial - Math.floor(serial);
    const totalMinutes = Math.round(frac * 1440);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return String(hours).padStart(2, '0') + ':' + String(mins).padStart(2, '0');
  };

  // Normalize any cell value to a displayable string (handles Date objects, serial numbers, strings)
  const normalizeCellValue = (val) => {
    if (val === null || val === undefined) return '';
    if (val instanceof Date) {
      const y = val.getFullYear();
      const m = String(val.getMonth() + 1).padStart(2, '0');
      const d = String(val.getDate()).padStart(2, '0');
      const h = String(val.getHours()).padStart(2, '0');
      const min = String(val.getMinutes()).padStart(2, '0');
      if (h === '00' && min === '00') return `${y}-${m}-${d}`;
      return `${y}-${m}-${d}T${h}:${min}`;
    }
    return String(val);
  };

  // Extract date part from a cell value
  const extractDate = (val) => {
    if (val === null || val === undefined) return '';
    if (val instanceof Date) {
      return val.getFullYear() + '-' + String(val.getMonth() + 1).padStart(2, '0') + '-' + String(val.getDate()).padStart(2, '0');
    }
    if (typeof val === 'number') {
      return excelSerialToDate(val) || '';
    }
    const s = String(val).trim();
    // Already YYYY-MM-DD or YYYY-MM-DDTHH:MM
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    // Try MM/DD/YYYY
    const mdy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
    if (mdy) return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
    // Try DD/MM/YYYY
    const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
    return s;
  };

  // Extract time part from a cell value
  const extractTime = (val) => {
    if (val === null || val === undefined) return '';
    if (val instanceof Date) {
      return String(val.getHours()).padStart(2, '0') + ':' + String(val.getMinutes()).padStart(2, '0');
    }
    if (typeof val === 'number') {
      // Pure time fraction (< 1) or datetime serial
      if (val < 1) return excelSerialToTime(val) || '';
      return excelSerialToTime(val) || '';
    }
    const s = String(val).trim();
    // Extract HH:MM from string
    const tm = s.match(/(\d{1,2}):(\d{2})/);
    if (tm) return tm[1].padStart(2, '0') + ':' + tm[2];
    return s;
  };

  const DEFAULT_FIELD_MAP = {
    date_col: ['date', 'date/time', 'date / time', 'datetime', 'timestamp', 'dt'],
    time_col: ['time', 'hour', 'clock'],
    from_callsign: ['from', 'from callsign', 'from_callsign', 'sender', 'from call'],
    from_msg_number: ['from msg', 'from msg #', 'from_msg_number', 'from msg number', 'from #'],
    to_callsign: ['to', 'to callsign', 'to_callsign', 'recipient', 'to call'],
    to_msg_number: ['to msg', 'to msg #', 'to_msg_number', 'to msg number', 'to #'],
    message: ['message', 'msg', 'content', 'text', 'description', 'notes', 'details']
  };

  const autoMapColumns = (headers) => {
    const mapping = {};
    const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
    Object.entries(DEFAULT_FIELD_MAP).forEach(([field, aliases]) => {
      const idx = normalizedHeaders.findIndex(h => aliases.includes(h));
      if (idx !== -1) mapping[field] = idx;
    });
    return mapping;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: 'binary', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });
        // Also get raw data for proper date handling
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true });
        if (jsonData.length < 2) {
          alert('File must have a header row and at least one data row.');
          return;
        }
        const headers = jsonData[0].map(h => String(h || ''));
        const rows = rawData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));
        const mapping = autoMapColumns(headers);
        setImportPreview({ headers, rows, mapping });
        setImportModalOpen(true);
      } catch (err) {
        alert('Error reading file: ' + err.message);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const handleImportConfirm = () => {
    const { rows, mapping } = importPreview;
    const newItems = rows.map(row => {
      // Build the combined date/time value
      let dateTimeCombined = '';
      const dateVal = mapping.date_col !== undefined ? row[mapping.date_col] : undefined;
      const timeVal = mapping.time_col !== undefined ? row[mapping.time_col] : undefined;

      if (dateVal !== undefined && timeVal !== undefined) {
        // Separate date and time columns - combine them
        const datePart = extractDate(dateVal);
        const timePart = extractTime(timeVal);
        dateTimeCombined = datePart && timePart ? `${datePart}T${timePart}` : datePart || timePart || '';
      } else if (dateVal !== undefined) {
        // Single date/time column - extract both parts
        const datePart = extractDate(dateVal);
        const timePart = extractTime(dateVal);
        dateTimeCombined = datePart && timePart && timePart !== '00:00' ? `${datePart}T${timePart}` : datePart || '';
      } else if (timeVal !== undefined) {
        // Only time column
        dateTimeCombined = extractTime(timeVal);
      }

      return {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
        time: dateTimeCombined,
        from_callsign: mapping.from_callsign !== undefined ? normalizeCellValue(row[mapping.from_callsign]) : '',
        from_msg_number: mapping.from_msg_number !== undefined ? normalizeCellValue(row[mapping.from_msg_number]) : '',
        to_callsign: mapping.to_callsign !== undefined ? normalizeCellValue(row[mapping.to_callsign]) : '',
        to_msg_number: mapping.to_msg_number !== undefined ? normalizeCellValue(row[mapping.to_msg_number]) : '',
        message: mapping.message !== undefined ? normalizeCellValue(row[mapping.message]) : ''
      };
    });
    setFormData(prev => ({
      ...prev,
      message_log: [...prev.message_log, ...newItems]
    }));
    setImportModalOpen(false);
    setImportPreview({ headers: [], rows: [], mapping: {} });
    setHasUnsavedChanges(true);
  };

  const updateMapping = (field, colIndex) => {
    setImportPreview(prev => ({
      ...prev,
      mapping: { ...prev.mapping, [field]: colIndex === '' ? undefined : parseInt(colIndex) }
    }));
  };

  // Save ICS 309 entry
  const saveEntry = async () => {
    if (!currentExercise?.id) {
      alert('No exercise selected');
      return;
    }

    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const payload = {
        ...formData,
        exercise_id: currentExercise.id,
        exercise_name: currentExercise.exercise_name
      };

      let response;
      if (editingEntry) {
        response = await fetch(`${API}/ics-309/${editingEntry.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/ics-309`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        setLastSaved(new Date().toISOString());
        setHasUnsavedChanges(false);
        alert(editingEntry ? 'ICS 309 entry updated successfully!' : 'ICS 309 entry created successfully!');
        fetchEntries();
        setView('list');
        setEditingEntry(null);
      } else {
        alert('Failed to save ICS 309 entry');
      }
    } catch (error) {
      console.error('Error saving ICS 309 entry:', error);
      alert('Error saving ICS 309 entry');
    } finally {
      setLoading(false);
    }
  };

  // Delete ICS 309 entry
  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this ICS 309 entry?')) return;

    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/ics-309/${entryId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        alert('ICS 309 entry deleted successfully!');
        fetchEntries();
      } else {
        alert('Failed to delete ICS 309 entry');
      }
    } catch (error) {
      console.error('Error deleting ICS 309 entry:', error);
      alert('Error deleting ICS 309 entry');
    } finally {
      setLoading(false);
    }
  };

  // Print function
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const currentDateTime = new Date().toLocaleString();
    
    const formatDateTime = (dateTimeStr) => {
      if (!dateTimeStr) return 'N/A';
      const [date, time] = dateTimeStr.split('T');
      if (!date) return 'N/A';
      return `${date} ${time || ''}`.trim();
    };
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>ICS 309 - Communications Log</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.4; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
            .header h1 { color: #f97316; margin: 0 0 10px 0; font-size: 24px; }
            .header p { margin: 5px 0; }
            .info-section { margin: 20px 0; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin: 15px 0; }
            .info-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 15px 0; }
            .info-item { border: 1px solid #ddd; padding: 10px; background: #f9f9f9; }
            .info-label { font-weight: bold; color: #f97316; margin-bottom: 5px; font-size: 12px; text-transform: uppercase; }
            .info-value { font-size: 14px; }
            .section-title { color: #f97316; font-weight: bold; font-size: 16px; margin: 25px 0 10px 0; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
            .message-table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 11px; }
            .message-table th, .message-table td { border: 1px solid #ddd; padding: 8px; text-align: left; vertical-align: top; }
            .message-table th { background-color: #f97316; color: white; font-weight: bold; }
            .message-table tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #666; border-top: 2px solid #ddd; padding-top: 15px; }
            @media print { 
              body { margin: 0; padding: 15px; }
              .message-table { page-break-inside: auto; }
              .message-table tr { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 309 - COMMUNICATIONS LOG</h1>
            <p><strong>Exercise:</strong> ${formData.exercise_name || currentExercise?.exercise_name || 'N/A'}</p>
            <p><strong>Generated:</strong> ${currentDateTime}</p>
          </div>
          
          <div class="info-section">
            <div class="section-title">Operational Period</div>
            <div class="info-grid-4">
              <div class="info-item">
                <div class="info-label">From:</div>
                <div class="info-value">${formatDateTime(formData.operational_period_from)}</div>
              </div>
              <div class="info-item">
                <div class="info-label">To:</div>
                <div class="info-value">${formatDateTime(formData.operational_period_to)}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Radio Network Name:</div>
                <div class="info-value">${formData.radio_network_name || 'N/A'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Operators:</div>
                <div class="info-value">${(formData.radio_operators || []).length || (formData.radio_operator ? 1 : 0)}</div>
              </div>
            </div>
          </div>

          ${(formData.radio_operators && formData.radio_operators.length > 0) ? `
            <div class="section-title">Radio Operators</div>
            <table class="message-table">
              <thead>
                <tr>
                  <th>Lastname</th><th>Firstname</th><th>Callsign</th><th>Location</th><th>Radio Type</th>
                </tr>
              </thead>
              <tbody>
                ${formData.radio_operators.map(op => `
                  <tr>
                    <td>${op.last_name || ''}</td>
                    <td>${op.first_name || ''}</td>
                    <td>${op.callsign || ''}</td>
                    <td>${op.location || ''}</td>
                    <td>${op.radio_type || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : (formData.radio_operator ? `
            <div class="section-title">Radio Operator</div>
            <div class="info-item"><div class="info-value">${formData.radio_operator}</div></div>
          ` : '')}

          <div class="section-title">Message / Log</div>
          <table class="message-table">
            <thead>
              <tr>
                <th style="width: 80px;">Time</th>
                <th style="width: 60px;">Msg #</th>
                <th style="width: 100px;">From Callsign</th>
                <th style="width: 100px;">To Callsign</th>
                <th>Message</th>
                <th style="width: 130px;">Action</th>
              </tr>
            </thead>
            <tbody>
              ${formData.message_log && formData.message_log.length > 0 
                ? formData.message_log.map(item => `
                    <tr>
                      <td>${formatDateTime(item.time)}</td>
                      <td>${item.from_msg_number || ''}</td>
                      <td>${item.from_callsign || ''}</td>
                      <td>${item.to_callsign || ''}</td>
                      <td>${item.message || ''}</td>
                      <td>${item.action_type || ''}</td>
                    </tr>
                  `).join('')
                : '<tr><td colspan="6" style="text-align: center; color: #666; padding: 20px;">No messages logged</td></tr>'
              }
            </tbody>
          </table>

          <div class="section-title">Prepared By</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Prepared By:</div>
              <div class="info-value">${formData.prepared_by || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Signature:</div>
              <div class="info-value">${formData.signature || 'N/A'}</div>
            </div>
          </div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Date/Time Prepared:</div>
              <div class="info-value">${formatDateTime(formData.date_time_prepared)}</div>
            </div>
          </div>
          
          <div class="footer">
            <div><strong>ICS 309 - Communications Log</strong></div>
            <div>Total Messages: ${formData.message_log?.length || 0}</div>
            <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
          </div>
        </body>
      </html>
    `;
    
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Print all Communications Logs across the exercise in one document.
  // Each entry (operational period) becomes its own section, so the top-level
  // Print ICS 309 button produces a consolidated report rather than a single log.
  const handlePrintAll = () => {
    if (!entries || entries.length === 0) return;
    const printWindow = window.open('', '_blank');
    const currentDateTime = new Date().toLocaleString();

    const formatDateTime = (dateTimeStr) => {
      if (!dateTimeStr) return 'N/A';
      const [date, time] = dateTimeStr.split('T');
      if (!date) return 'N/A';
      return `${date} ${time || ''}`.trim();
    };

    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const totalMessages = entries.reduce((sum, e) => sum + ((e.message_log || []).length), 0);

    const logSection = (entry, idx) => {
      const ops = entry.radio_operators || [];
      return `
      <div class="log-block">
        <h2 class="log-title">Log ${idx + 1} of ${entries.length}${entry.radio_network_name ? ` — ${escapeHtml(entry.radio_network_name)}` : ''}</h2>
        <div class="info-grid-4">
          <div class="info-item"><div class="info-label">From</div><div class="info-value">${formatDateTime(entry.operational_period_from)}</div></div>
          <div class="info-item"><div class="info-label">To</div><div class="info-value">${formatDateTime(entry.operational_period_to)}</div></div>
          <div class="info-item"><div class="info-label">Radio Network Name</div><div class="info-value">${escapeHtml(entry.radio_network_name || 'N/A')}</div></div>
          <div class="info-item"><div class="info-label">Operators</div><div class="info-value">${ops.length || (entry.radio_operator ? 1 : 0)}</div></div>
        </div>
        ${ops.length > 0 ? `
          <table class="message-table" style="margin-top:8px;">
            <thead>
              <tr>
                <th>Lastname</th><th>Firstname</th><th>Callsign</th><th>Location</th><th>Radio Type</th>
              </tr>
            </thead>
            <tbody>
              ${ops.map(op => `
                <tr>
                  <td>${escapeHtml(op.last_name)}</td>
                  <td>${escapeHtml(op.first_name)}</td>
                  <td>${escapeHtml(op.callsign)}</td>
                  <td>${escapeHtml(op.location)}</td>
                  <td>${escapeHtml(op.radio_type)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : (entry.radio_operator ? `
          <div class="info-item" style="margin-top:6px;"><div class="info-label">Radio Operator</div><div class="info-value">${escapeHtml(entry.radio_operator)}</div></div>
        ` : '')}
        <table class="message-table">
          <thead>
            <tr>
              <th style="width: 80px;">Time</th>
              <th style="width: 60px;">Msg #</th>
              <th style="width: 100px;">From Callsign</th>
              <th style="width: 100px;">To Callsign</th>
              <th>Message</th>
              <th style="width: 130px;">Action</th>
            </tr>
          </thead>
          <tbody>
            ${(entry.message_log && entry.message_log.length > 0)
              ? entry.message_log.map(item => `
                  <tr>
                    <td>${formatDateTime(item.time)}</td>
                    <td>${escapeHtml(item.from_msg_number)}</td>
                    <td>${escapeHtml(item.from_callsign)}</td>
                    <td>${escapeHtml(item.to_callsign)}</td>
                    <td>${escapeHtml(item.message)}</td>
                    <td>${escapeHtml(item.action_type)}</td>
                  </tr>
                `).join('')
              : '<tr><td colspan="6" style="text-align:center;color:#666;padding:20px;">No messages logged</td></tr>'
            }
          </tbody>
        </table>
        <div class="info-grid" style="margin-top:12px;">
          <div class="info-item"><div class="info-label">Prepared By</div><div class="info-value">${escapeHtml(entry.prepared_by || 'N/A')}</div></div>
          <div class="info-item"><div class="info-label">Signature</div><div class="info-value">${escapeHtml(entry.signature || 'N/A')}</div></div>
          <div class="info-item"><div class="info-label">Date/Time Prepared</div><div class="info-value">${formatDateTime(entry.date_time_prepared)}</div></div>
          <div class="info-item"><div class="info-label">Messages in this Log</div><div class="info-value">${(entry.message_log || []).length}</div></div>
        </div>
      </div>
    `;
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>ICS 309 - Communications Log (All Entries)</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.4; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
            .header h1 { color: #f97316; margin: 0 0 10px 0; font-size: 24px; }
            .header p { margin: 5px 0; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin: 15px 0; }
            .info-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 15px 0; }
            .info-item { border: 1px solid #ddd; padding: 10px; background: #f9f9f9; }
            .info-label { font-weight: bold; color: #f97316; margin-bottom: 5px; font-size: 12px; text-transform: uppercase; }
            .info-value { font-size: 14px; }
            .log-block { margin: 30px 0; padding: 15px; border: 2px solid #f97316; border-radius: 6px; page-break-inside: avoid; }
            .log-block + .log-block { page-break-before: always; }
            .log-title { color: #f97316; margin: 0 0 12px 0; font-size: 18px; border-bottom: 1px solid #f97316; padding-bottom: 6px; }
            .message-table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 11px; }
            .message-table th, .message-table td { border: 1px solid #ddd; padding: 8px; text-align: left; vertical-align: top; }
            .message-table th { background-color: #f97316; color: white; font-weight: bold; }
            .message-table tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #666; border-top: 2px solid #ddd; padding-top: 15px; }
            @media print {
              body { margin: 0; padding: 15px; }
              .message-table { page-break-inside: auto; }
              .message-table tr { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 309 - COMMUNICATIONS LOG (All Entries)</h1>
            <p><strong>Exercise:</strong> ${escapeHtml(currentExercise?.exercise_name || formData.exercise_name || 'N/A')}</p>
            <p><strong>Generated:</strong> ${escapeHtml(currentDateTime)}</p>
            <p><strong>Total Logs:</strong> ${entries.length} &nbsp; · &nbsp; <strong>Total Messages:</strong> ${totalMessages}</p>
          </div>
          ${entries.map((entry, i) => logSection(entry, i)).join('')}
          <div class="footer">
            <div><strong>ICS 309 - Communications Log · Consolidated Report</strong></div>
            <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
          </div>
        </body>
      </html>
    `;

    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // List view
  if (view === 'list') {
    return (
      <div className="p-6 space-y-6">
        {/* Header with Sync Status */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>ICS 309 - Communications Log</h1>
            <p className={`${theme.colors.textMuted} text-lg`}>
              Track and document all communications for incident operations
            </p>
          </div>
          <div className="flex gap-3 items-center">
            <SyncHeader
              lastSaved={lastSaved}
              hasUnsavedChanges={hasUnsavedChanges}
              onRefresh={handleRefresh}
              loading={loading}
              formName="ICS 309"
            />
            <Button
              onClick={handlePrintAll}
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              disabled={entries.length === 0}
              title="Print all communications logs with every message entry"
              data-testid="ics309-print-all-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              Print ICS 309
            </Button>
            <input
              ref={logFileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleLogImportFile}
              className="hidden"
              data-testid="ics309-log-import-file"
            />
            <Button
              onClick={handleLogImportClick}
              variant="outline"
              className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
              title="Import Comms Log entries from CSV"
              data-testid="ics309-log-import-csv"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={handleLogExportCSV}
              variant="outline"
              className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
              disabled={entries.length === 0}
              title="Export all Comms Log entries as CSV"
              data-testid="ics309-log-export-csv"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              onClick={() => { setEditingEntry(null); setView('form'); }}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Entry
            </Button>
          </div>
        </div>

        {/* Entries List */}
        {loading ? (
          <div className="text-center py-12">
            <p className={`${theme.colors.textMuted}`}>Loading...</p>
          </div>
        ) : entries.length > 0 ? (
          <div className="space-y-4">
            {entries.map((entry) => (
              <Card key={entry.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-amber-500">
                        {entry.radio_network_name || 'Untitled Entry'}
                      </CardTitle>
                      <CardDescription className={`${theme.colors.textMuted} mt-1`}>
                        {(entry.radio_operators && entry.radio_operators.length > 0)
                          ? `Operators: ${entry.radio_operators.length} · `
                          : (entry.radio_operator ? `Operator: ${entry.radio_operator} · ` : 'Operator: Not set · ')}
                        Period: {entry.operational_period_from ? entry.operational_period_from.replace('T', ' ') : 'N/A'} → {entry.operational_period_to ? entry.operational_period_to.replace('T', ' ') : 'N/A'}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditingEntry(entry);
                          setFormData({
                            exercise_name: entry.exercise_name || currentExercise?.exercise_name || '',
                            operational_period_from: entry.operational_period_from || '',
                            operational_period_to: entry.operational_period_to || '',
                            radio_network_name: entry.radio_network_name || '',
                            radio_operator: entry.radio_operator || '',
                            radio_operators: entry.radio_operators || [],
                            message_log: entry.message_log || [],
                            prepared_by: entry.prepared_by || '',
                            signature: entry.signature || '',
                            date_time_prepared: entry.date_time_prepared || ''
                          });
                          setTimeout(() => handlePrint(), 100);
                        }}
                        size="sm"
                        variant="outline"
                        className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Print ICS 309
                      </Button>
                      <Button
                        onClick={() => {
                          setEditingEntry(entry);
                          setView('form');
                        }}
                        size="sm"
                        variant="outline"
                        className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => deleteEntry(entry.id)}
                        size="sm"
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm">
                    <p className={`${theme.colors.textMuted}`}>
                      Total Messages: {entry.message_log?.length || 0}
                    </p>
                    <p className={`${theme.colors.textMuted} text-xs mt-1`}>
                      Created: {entry.created_at ? new Date(entry.created_at).toLocaleString() : 'N/A'}
                      {entry.updated_at && entry.updated_at !== entry.created_at && (
                        <> | Updated: {new Date(entry.updated_at).toLocaleString()}</>
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
            <CardContent className="text-center py-12">
              <FileText className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-4`}>No Entries Created</h3>
              <p className={`${theme.colors.textMuted} mb-6`}>
                Create your first ICS 309 Communications Log entry to get started.
              </p>
              <Button 
                onClick={() => { setEditingEntry(null); setView('form'); }}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Entry
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Form view
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold text-white mb-2`}>
            {editingEntry ? 'Edit ICS 309' : 'Create ICS 309 - Communications Log'}
          </h1>
          <p className={`${theme.colors.textMuted} text-lg`}>
            Document radio communications, message logs, and operator details
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handlePrint}
            variant="outline"
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
          >
            <FileText className="h-4 w-4 mr-2" />
            Print ICS 309
          </Button>
          <Button 
            onClick={() => {
              setView('list');
              setEditingEntry(null);
            }}
            variant="outline"
            className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
        </div>
      </div>

      {/* First Card - Basic Information */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-amber-500">Basic Information</CardTitle>
            <Button onClick={saveEntry} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white" size="sm">
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* First Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Exercise Name</Label>
                <div className={`p-2 bg-gray-800 rounded border border-gray-700`}>
                  <span className={`text-white`}>{currentExercise?.exercise_name || 'N/A'}</span>
                </div>
              </div>
              <div className="md:col-span-3 grid grid-cols-2 gap-4">
                <div>
                  <Label className={`text-white text-xs font-medium mb-1 block`}>From:</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={formData.operational_period_from ? formData.operational_period_from.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = formData.operational_period_from ? formData.operational_period_from.split('T')[1] : '00:00';
                        setFormData(prev => ({ ...prev, operational_period_from: `${date}T${time}` }));
                      }}
                      className={`bg-gray-800 border-gray-600 text-white`}
                    />
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      pattern="[0-2][0-9]:[0-5][0-9]"
                      maxLength="5"
                      value={formData.operational_period_from ? formData.operational_period_from.split('T')[1] : ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9:]/g, '');
                        if (value.length === 2 && !value.includes(':')) {
                          value = value + ':';
                        }
                        if (value.length <= 5) {
                          const date = formData.operational_period_from ? formData.operational_period_from.split('T')[0] : '';
                          setFormData(prev => ({ ...prev, operational_period_from: `${date}T${value}` }));
                        }
                      }}
                      className={`bg-gray-800 border-gray-600 text-white`}
                    />
                  </div>
                </div>
                <div>
                  <Label className={`text-white text-xs font-medium mb-1 block`}>To:</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={formData.operational_period_to ? formData.operational_period_to.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = formData.operational_period_to ? formData.operational_period_to.split('T')[1] : '00:00';
                        setFormData(prev => ({ ...prev, operational_period_to: `${date}T${time}` }));
                      }}
                      className={`bg-gray-800 border-gray-600 text-white`}
                    />
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      pattern="[0-2][0-9]:[0-5][0-9]"
                      maxLength="5"
                      value={formData.operational_period_to ? formData.operational_period_to.split('T')[1] : ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9:]/g, '');
                        if (value.length === 2 && !value.includes(':')) {
                          value = value + ':';
                        }
                        if (value.length <= 5) {
                          const date = formData.operational_period_to ? formData.operational_period_to.split('T')[0] : '';
                          setFormData(prev => ({ ...prev, operational_period_to: `${date}T${value}` }));
                        }
                      }}
                      className={`bg-gray-800 border-gray-600 text-white`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Radio Network Name</Label>
                <Input
                  value={formData.radio_network_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, radio_network_name: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>

            {/* Radio Operators (repeater) */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-white text-xs font-semibold uppercase tracking-wide">Radio Operators</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    radio_operators: [
                      ...(prev.radio_operators || []),
                      { id: `op-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, last_name: '', first_name: '', callsign: '', location: '', radio_type: '' }
                    ]
                  }))}
                  data-testid="ics309-add-operator-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Operator
                </Button>
              </div>
              {(formData.radio_operators || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic">No radio operators added yet. Click "Add Operator" to add one.</p>
              ) : (
                <div className="space-y-2">
                  {(formData.radio_operators || []).map((op, i) => (
                    <div key={op.id || i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end bg-gray-800/40 p-2 rounded border border-gray-700" data-testid={`ics309-operator-row-${i}`}>
                      <div className="md:col-span-2">
                        <Label className="text-[10px] text-gray-400">Lastname</Label>
                        <Input value={op.last_name || ''}
                          onChange={(e) => setFormData(prev => {
                            const arr = [...(prev.radio_operators || [])];
                            arr[i] = { ...arr[i], last_name: e.target.value };
                            return { ...prev, radio_operators: arr };
                          })}
                          className="bg-gray-900 border-gray-600 text-white h-8 text-sm" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-[10px] text-gray-400">Firstname</Label>
                        <Input value={op.first_name || ''}
                          onChange={(e) => setFormData(prev => {
                            const arr = [...(prev.radio_operators || [])];
                            arr[i] = { ...arr[i], first_name: e.target.value };
                            return { ...prev, radio_operators: arr };
                          })}
                          className="bg-gray-900 border-gray-600 text-white h-8 text-sm" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-[10px] text-gray-400">Callsign</Label>
                        <Input value={op.callsign || ''}
                          onChange={(e) => setFormData(prev => {
                            const arr = [...(prev.radio_operators || [])];
                            arr[i] = { ...arr[i], callsign: e.target.value };
                            return { ...prev, radio_operators: arr };
                          })}
                          className="bg-gray-900 border-gray-600 text-white h-8 text-sm" />
                      </div>
                      <div className="md:col-span-3">
                        <Label className="text-[10px] text-gray-400">Location</Label>
                        <Input value={op.location || ''}
                          onChange={(e) => setFormData(prev => {
                            const arr = [...(prev.radio_operators || [])];
                            arr[i] = { ...arr[i], location: e.target.value };
                            return { ...prev, radio_operators: arr };
                          })}
                          className="bg-gray-900 border-gray-600 text-white h-8 text-sm" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-[10px] text-gray-400">Radio Type</Label>
                        <select value={op.radio_type || ''}
                          onChange={(e) => setFormData(prev => {
                            const arr = [...(prev.radio_operators || [])];
                            arr[i] = { ...arr[i], radio_type: e.target.value };
                            return { ...prev, radio_operators: arr };
                          })}
                          className="w-full h-8 bg-gray-900 border border-gray-600 text-white text-sm rounded px-2"
                          data-testid={`ics309-operator-type-${i}`}>
                          <option value="">--</option>
                          <option value="Base Stn">Base Stn</option>
                          <option value="Mobile">Mobile</option>
                          <option value="Handheld">Handheld</option>
                          <option value="Cellphone">Cellphone</option>
                        </select>
                      </div>
                      <div className="md:col-span-1 flex justify-end">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            radio_operators: (prev.radio_operators || []).filter((_, idx) => idx !== i)
                          }))}
                          title="Remove operator"
                          data-testid={`ics309-remove-operator-${i}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Message Log Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-amber-500">Message / Log</CardTitle>
            <div className="flex gap-2">
              <Button onClick={saveEntry} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white" size="sm">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-blue-500 text-blue-400 hover:bg-blue-500/10" size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Import CSV/Excel
              </Button>
              <Button onClick={addMessageLogItem} variant="outline" className="border-green-500 text-green-500 hover:bg-green-500/10" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Message
              </Button>
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" />
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Date / Time</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Msg #</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>From<br/>Callsign</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>To<br/>Callsign</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Message</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Action</th>
                    <th className={`${theme.colors.textSecondary} text-center p-2 print:hidden`}></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.message_log.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <Input
                            type="date"
                            value={item.time ? item.time.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = item.time && item.time.includes('T') ? item.time.split('T')[1] : '';
                              updateMessageLogItem(index, 'time', date ? `${date}T${time}` : '');
                            }}
                            className="bg-gray-800 border-gray-600 text-white w-36"
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={item.time && item.time.includes('T') ? item.time.split('T')[1] : (item.time || '')}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = item.time && item.time.includes('T') ? item.time.split('T')[0] : '';
                                updateMessageLogItem(index, 'time', date ? `${date}T${value}` : value);
                              }
                            }}
                            className="bg-gray-800 border-gray-600 text-white w-20"
                          />
                        </div>
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.from_msg_number}
                          onChange={(e) => updateMessageLogItem(index, 'from_msg_number', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-20`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.from_callsign}
                          onChange={(e) => updateMessageLogItem(index, 'from_callsign', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-24`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.to_callsign}
                          onChange={(e) => updateMessageLogItem(index, 'to_callsign', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-24`}
                        />
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={item.message}
                          onChange={(e) => updateMessageLogItem(index, 'message', e.target.value)}
                          rows={1}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[200px]`}
                        />
                      </td>
                      <td className="p-2 print:hidden">
                        <Select
                          value={item.action_type || ''}
                          onValueChange={(value) => updateMessageLogItem(index, 'action_type', value)}
                        >
                          <SelectTrigger className="bg-gray-800 border-gray-600 text-white min-w-[180px]" data-testid={`ics309-action-select-${index}`}>
                            <SelectValue placeholder="Select from list" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            <SelectItem value="Net now active" className="text-white focus:bg-gray-700">Net now active</SelectItem>
                            <SelectItem value="Change Frequency" className="text-white focus:bg-gray-700">Change Frequency</SelectItem>
                            <SelectItem value="New Net Controller and/or Callsign" className="text-white focus:bg-gray-700">New Net Controller and/or Callsign</SelectItem>
                            <SelectItem value="Net Pause - 10 min" className="text-white focus:bg-gray-700">Net Pause - 10 min</SelectItem>
                            <SelectItem value="Emergency Traffic" className="text-white focus:bg-gray-700">Emergency Traffic</SelectItem>
                            <SelectItem value="Significant Event" className="text-white focus:bg-gray-700">Significant Event</SelectItem>
                            <SelectItem value="Net now closed" className="text-white focus:bg-gray-700">Net now closed</SelectItem>
                            <SelectItem value="Other" className="text-white focus:bg-gray-700">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 text-right print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteMessageLogItem(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          data-testid={`ics309-delete-msg-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 print:hidden flex gap-2">
              <Button onClick={addMessageLogItem} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Message
              </Button>
              <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-blue-500 text-blue-400 hover:bg-blue-500/10">
                <Upload className="h-4 w-4 mr-2" />
                Import CSV/Excel
              </Button>
            </div>

            {/* Import Preview Modal */}
            {importModalOpen && (
              <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setImportModalOpen(false)}>
                <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-4xl max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  <div className="p-6 border-b border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-white">Import Spreadsheet Data</h2>
                        <p className="text-gray-400 text-sm mt-1">{importPreview.rows.length} rows found. Map columns to fields below.</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setImportModalOpen(false)} className="border-gray-600 text-gray-400 hover:bg-gray-700">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
                    {/* Column Mapping */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { key: 'date_col', label: 'Date' },
                        { key: 'time_col', label: 'Time (24hr)' },
                        { key: 'from_callsign', label: 'From Callsign' },
                        { key: 'from_msg_number', label: 'From Msg #' },
                        { key: 'to_callsign', label: 'To Callsign' },
                        { key: 'to_msg_number', label: 'To Msg #' },
                        { key: 'message', label: 'Message' }
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <label className="text-sm text-gray-300 block mb-1">{label}</label>
                          <select
                            value={importPreview.mapping[key] !== undefined ? importPreview.mapping[key] : ''}
                            onChange={(e) => updateMapping(key, e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 text-sm"
                          >
                            <option value="">-- Skip --</option>
                            {importPreview.headers.map((h, i) => (
                              <option key={`ics309-${i}`} value={i}>{h}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>

                    {/* Preview Table */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-300 mb-2">Preview (first 5 rows)</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="border-b border-gray-600">
                              {importPreview.headers.map((h, i) => (
                                <th key={`ics309-${i}`} className="text-left p-2 text-amber-400 whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {importPreview.rows.slice(0, 5).map((row, ri) => (
                              <tr key={`preview-row-${ri}`} className="border-b border-gray-700">
                                {importPreview.headers.map((_, ci) => (
                                  <td key={`preview-cell-${ri}-${ci}`} className="p-2 text-gray-300 whitespace-nowrap max-w-[200px] truncate">
                                    {row[ci] !== undefined ? String(row[ci]) : ''}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 border-t border-gray-700 flex justify-between items-center">
                    <p className="text-gray-400 text-sm">
                      {Object.values(importPreview.mapping).filter(v => v !== undefined).length} of 7 fields mapped
                    </p>
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setImportModalOpen(false)} className="border-gray-600 text-gray-300 hover:bg-gray-700">
                        Cancel
                      </Button>
                      <Button
                        onClick={handleImportConfirm}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        disabled={Object.values(importPreview.mapping).filter(v => v !== undefined).length === 0}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Import {importPreview.rows.length} Rows
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prepared By Section */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-amber-500">Prepared By</CardTitle>
            <Button onClick={saveEntry} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white" size="sm">
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Prepared By</Label>
                <Input
                  value={formData.prepared_by}
                  onChange={(e) => setFormData(prev => ({ ...prev, prepared_by: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Signature</Label>
                <Input
                  value={formData.signature}
                  onChange={(e) => setFormData(prev => ({ ...prev, signature: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Date/Time Prepared</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.date_time_prepared ? formData.date_time_prepared.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_time_prepared ? formData.date_time_prepared.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, date_time_prepared: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_time_prepared ? formData.date_time_prepared.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_time_prepared ? formData.date_time_prepared.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, date_time_prepared: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            onClick={saveEntry}
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-white flex-1"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingEntry ? 'Update Entry' : 'Save Entry')}
          </Button>
          <Button 
            onClick={() => {
              setView('list');
              setEditingEntry(null);
            }}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Cancel
          </Button>
        </div>
    </div>
  );
};



// Food Unit Form

export default ICS309Form;
